<template>
  <div
    data-test="logic-tree"
    class="pl-2 logic-tree"
    :data-depth="depth"
    :class="{ 'border rounded-5 pt-2': depth===0, 'pb-2': isNode }"
  >
    <div
      class="d-flex align-items-center"
      :class="{ 'pb-2': !isNode }"
    >
      <div
        v-if="isNode"
        class="flex-1"
      >
        <slot v-bind="{ value, update: triggerValueUpdate, editable }" />
      </div>
      <div
        v-else
        class="flex-1 "
      >
        <FormSelect
          :disabled="!editable"
          :config="{ options: [ 'all', 'any' ] }"
          :customLabel="conditionCustomLabel"
          :value="value.condition"
          data-test="select-logic-condition"
          class="font-weight-bold"
          @input="triggerValueUpdate({...value, condition: $event })"
        />
      </div>
      <div
        v-if="editable"
        class="button-group d-flex mx-2 rounded-5 border bg-white"
      >
        <AppTooltip>
          <AppButton
            v-if="isConvertable && ![allowedDepth, 0].includes(depth)"
            class="p-1 border-right"
            data-test="convert-logic-rule"
            @click="triggerConvertRule"
          >
            <AppIcon name="loop" />
          </AppButton>
          <template #tooltip>
            <span>Convert into {{ isNode ? 'group':'single rule' }}</span>
          </template>
        </AppTooltip>
        <AppTooltip>
          <AppButton
            v-if="!isNode"
            class="p-1"
            data-test="add-logic-rule"
            :class="{ 'border-right': depth !== 0 }"
            @click="addChildRule"
          >
            <AppIcon name="add" />
          </AppButton>
          <template #tooltip>
            <span>Add new child rule</span>
          </template>
        </AppTooltip>
        <AppTooltip>
          <AppButton
            v-if="depth!==0"
            class="p-1"
            data-test="remove-logic-rule"
            @click="triggerDeleteRule"
          >
            <AppIcon name="remove" />
          </AppButton>
          <template #tooltip>
            <span>Delete this rule including any of its children</span>
          </template>
        </AppTooltip>
      </div>
    </div>
    <div
      v-if="!isNode && value.rules.length"
      class="position-relative"
    >
      <div class="pointer-line border border-right-0 border-top-0" />
      <div
        class="border border-right-0 border-bottom-0 border-curved ml-2"
        :style="{ background: depth % 2 === 0 ? '#f5f5f5' : '#ffffff' }"
      >
        <LogicTree
          v-for="(rule,index) in value.rules"
          :key="index"
          :value="rule"
          :depth="depth+1"
          :defaultNewItem="defaultNewItem"
          :editable="editable"
          :class="{ 'pt-2' : index === 0, 'pb-0': index === value.rules.length-1 && !rule.rules, 'pb-2': index !== value.rules.length-1}"
          @input="updateRule(index, $event)"
          @delete="deleteRule(index)"
        >
          <template #default="{ value: rule, update: nestedUpdate }">
            <slot v-bind="{ value: rule, update: nestedUpdate, editable }" />
          </template>
        </LogicTree>
      </div>
    </div>
  </div>
</template>

<script>
import { putAt } from '@shared/utils/array'
import FormSelect from '@/components/Forms/FormSelect'

export default {
  name: 'LogicTree',
  components: {
    FormSelect,
  },
  props: {
    value: {
      type: [ Object, Array ],
      default: () => ({}),
    },
    depth: {
      type: Number,
      default: 0,
    },
    allowedDepth: {
      type: Number,
      default: 3,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    defaultNewItem: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    isNode () {
      return !this.value.condition
    },
    isConvertable () {
      return this.isNode || this.value?.rules?.length === 1
    },
  },
  methods: {
    conditionCustomLabel (value) {
      return `${this.decamelize(value)} of`
    },
    deleteRule (index) {
      this.$confirm({
        message: 'Are you sure you want to delete this rule?',
        onConfirm: async close => {
          this.triggerValueUpdate({ ...this.value, rules: this.value.rules.filter((e, i) => index !== i) })
          this.$nextTick(() => {
            if (this.value.rules.length === 0) this.triggerDeleteRule() // Delete the parent if no rule exist
            close()
          })
        },
        onCancel: close => close(),
      })
    },
    updateRule (index, rule) {
      this.updateRules(putAt(
        this.value.rules,
        index,
        rule
      ))
    },
    addChildRule () {
      this.triggerValueUpdate({ ...this.value,
        rules: [
          ...(this.value.rules || []),
          {
            ...this.defaultNewItem,
          },
        ],
      })
    },
    updateRules (rules) {
      this.triggerValueUpdate({ condition: this.value.condition, rules })
    },
    triggerDeleteRule () {
      this.triggerValueUpdate(this.value, 'delete')
    },
    triggerConvertRule () {
      if (this.isNode) {
        this.triggerValueUpdate({ condition: 'all', rules: [ this.value ] })
      } else {
        this.triggerValueUpdate(this.value.rules[0])
      }
    },
    triggerValueUpdate (value, event = 'input') {
      this.$emit(event, value)
    },
  },
}
</script>

<style scoped>
.logic-tree >>> .multiselect {
  margin-bottom: 0px !important;
}

.border-curved {
  border-radius: 5px 0 0 5px;
}

.pointer-line {
  position: absolute;
  height: calc(50% + 12px);
  left: 0px;
  width: 8px;
  top: -12px;
}
</style>
